import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import {
  checkPermissions,
  PRODUCTS_WRITE,
  LOYALTY_WRITE,
  SHOPS_WRITE,
  CAMPAIGNS_WRITE,
} from '../../shared/utils/permissions';
import { useUserPermissions } from '../../apis/user-details';
import { useTranslation } from 'react-i18next';

const SCHEDULE_LENGTH = 7;

/**
 * Stands in for "no return date" inside the select, which can only hold strings. Reported to the
 * caller as `null` - a translated label must never end up in the data.
 */
const INDEFINITE_VALUE = 'indefinite';

type Option = {
  value: string;
  label: string;
};

type Props = {
  value: string | null | undefined;
  /** Receives the chosen return date, or `null` for open-ended. */
  onChange: (value: string | null) => void;
  /**
   * Whether the open-ended ("Indefinitely") option may be offered. Admins get it by default, but it
   * must be hidden for entries that already exist in the backend with a scheduledUntil: the update
   * endpoint cannot clear a schedule back to open-ended, so picking it could not be saved.
   */
  allowIndefinite?: boolean;
};

const OutOfStockScheduleSelect: React.FC<Props> = ({ onChange, value, allowIndefinite = true }) => {
  const { roles: userPermissions } = useUserPermissions();
  const { t } = useTranslation();
  const hasAdminPermissions = useMemo(
    () => checkPermissions(userPermissions, [PRODUCTS_WRITE, LOYALTY_WRITE, SHOPS_WRITE, CAMPAIGNS_WRITE]),
    [userPermissions]
  );

  const isIndefiniteOffered = hasAdminPermissions && allowIndefinite;

  const today = dayjs().local().hour(0).minute(0).second(0);

  const options: Option[] = [];

  for (let index = 1; index <= SCHEDULE_LENGTH; index++) {
    const option: Option = {
      label: `${today.add(index, 'days').format('D.M.')}`,
      value: today.add(index, 'days').format(),
    };
    options.push(option);
  }

  if (isIndefiniteOffered) {
    options.push({ label: t('Indefinitely'), value: INDEFINITE_VALUE });
  }

  const handleChange = (event: SelectChangeEvent) => {
    const selected = event.target.value;
    onChange(selected === INDEFINITE_VALUE ? null : selected);
  };

  // Fully controlled by the caller, so what the box shows is always what will be saved. An entry
  // without a return date is open-ended, which is only shown as such where that option is offered.
  const selectedValue = value ?? (isIndefiniteOffered ? INDEFINITE_VALUE : '');
  // A stored date that is not one of the offered days (further out than SCHEDULE_LENGTH, or a
  // slightly different timestamp format) has no matching option; blank it rather than hand the
  // Select an out-of-range value. The "Scheduled until" column still shows the real date.
  const displayedValue = options.some((option) => option.value === selectedValue) ? selectedValue : '';

  return (
    <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
      <InputLabel id="out-of-stock-schedule-label">{t('Select schedule')}</InputLabel>
      <Select
        labelId="out-of-stock-schedule-label"
        id="out-of-stock-schedule-select"
        value={displayedValue}
        label={t('Select schedule')}
        onChange={handleChange}
      >
        {options.map((option) => (
          <MenuItem value={option.value} key={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default OutOfStockScheduleSelect;
